import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import {
  useDeleteDiscountMutation,
  useLazyGetDiscountListQuery,
  useToggleDiscountMutation,
} from "../../service/discount";
import { showError } from "../../constants/toasts";
import { OfferDetails } from "../../types/General";
import dayjs from "dayjs";
import Loader from "../../constants/Loader";
import WarnModal from "../../Modals/warnmodal";
import { showToast } from "../../constants/toasts";
import CustomPagination from "../../components/CustomPagination";
import { isValidInput } from "../../utils/validations";
import CurrencyIcon from "../../components/dirham";

const DiscountCoupon = () => {
  const navigate = useNavigate();

  const [getDiscountList] = useLazyGetDiscountListQuery();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [discount, setDiscount] = useState<OfferDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [deleteDiscountMethod] = useDeleteDiscountMutation();
  const [toggleDiscount] = useToggleDiscountMutation();
  const handleDelete = async (
    deleteById: any,
    selectedId: string,
    getAllData: () => void,
  ) => {
    if (selectedId) {
      try {
        const response = await deleteById({ id: selectedId }).unwrap();
        if (response?.statusCode === 200) {
          showToast(`Deleted Successfully`);
          if (getAllData) {
            getAllData();
          }
        } else {
          showError("Failed to delete");
        }
      } catch (error: any) {
        showError(error?.data?.message || "");
      }
    }
  };

  const getDiscountListApi = async (hideLoader = false) => {
    try {
      if (!hideLoader) setLoading(true);
      const response = await getDiscountList({
        page: page,
        limit: 10,
        search: debouncedSearchTerm,
      }).unwrap();
      if (!hideLoader) setLoading(false);
      if (response.statusCode === 200) {
        setDiscount(response?.data?.list || []);
        setCount(response?.data?.total || 0);
      } else {
        setDiscount([]);
      }
    } catch (error: any) {
      if (!hideLoader) setLoading(false);
      showError(error?.data?.message || "");
    }
  };
  const handleStatusChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    serviceId: string,
  ) => {
    const isActive = event.target.checked;

    try {
      const response = await toggleDiscount({
        id: serviceId,
        body: { isActive },
      }).unwrap();

      if (response?.statusCode === 200) {
        showToast("Status Updated Successfully");
        getDiscountListApi(true);
      } else {
        showError("Failed to update status");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Something went wrong");
    }
  };
  useEffect(() => {
    getDiscountListApi();
  }, [page, debouncedSearchTerm]);


  return (
    <>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1>Discount & Coupon Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Discount & Coupon Management
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => isValidInput(e.target.value) && setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              placeholder="Search coupons..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => navigate("/discount-coupon/add")}
            >
              Add Discount and Coupon
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Offer/Discount Name</TableCell>
                <TableCell>Coupon Code</TableCell>
                <TableCell>Discount Type</TableCell>
                <TableCell>Discount Value</TableCell>
                <TableCell>Applicable Services</TableCell>
                <TableCell>Valid From – To</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {discount.length > 0 ? (
                discount?.map((item: any, index) => (
                  <TableRow hover key={item?.id ?? index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item?.name}</TableCell>

                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(item.code);
                          showToast("Coupon code copied to clipboard!");
                        }}
                        sx={{
                          borderColor: "#00B67A",
                          color: "#00B67A",
                          fontWeight: 600,
                          borderRadius: "6px",
                          px: 1.5,
                          py: 0.2,
                        }}
                      >
                        {item.code}
                      </Button>
                    </TableCell>

                    <TableCell>
                      {item?.discountType == 2 ? "Flat" : "Percentage"}
                    </TableCell>

                    <TableCell>
                      {item?.discountType == 2 ? (
                        <>
                          <CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} />
                          {item?.discount}
                        </>
                      ) : (
                        item?.discount + "%"
                      )}
                    </TableCell>

                    <TableCell>
                      {item?.services?.length ? item.services.map((svc: any) => svc.name).join(", ") : "-"}
                    </TableCell>

                    <TableCell>
                      {item?.validFrom
                        ? dayjs(item?.validFrom).format("DD MMMM YYYY")
                        : "-"}{" "}
                      –{" "}
                      {item?.validTo
                        ? dayjs(item?.validTo).format("DD MMMM YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell>
                      {item?.updatedAt
                        ? dayjs(item?.date).format("DD MMMM YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell>
                      <SwitchToggle
                        checked={item?.isActive ?? item?.active}
                        onChange={(e) => handleStatusChange(e, item.id)}
                      />
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() =>
                            navigate(`/discount-coupon/view/${item.id}`)
                          }
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>

                        <IconButton
                          onClick={() =>
                            navigate(`/discount-coupon/edit/${item.id}`)
                          }
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>

                        <IconButton
                          onClick={() => {
                            setSelectedId(item?.id);
                            setOpen(true);
                          }}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No Coupons found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {count > 0 ? (
          <CustomPagination
            currentPage={page - 1}
            count={count}
            pageSize={10}
            onPageChange={(page) => setPage(page + 1)}
          />
        ) : null}
      </div>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() =>
          handleDelete(deleteDiscountMethod, selectedId, getDiscountListApi)
        }
        name="coupon"
      />
    </>
  );
};

export default DiscountCoupon;
